import { isPlatformBrowser, NgStyle } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ArgumentsLayoutEnum } from './arguments.enum';
import type { IArguments } from './arguments.interface';
import {
  BrandThemesEnum,
  DeviceService,
  IExtendedLink,
  ILinkMedia,
  ImageRatioEnum,
  IMedia,
  OpacityEnum,
  PositionEnum,
  SdBannerComponent,
  SdRichParagraphComponent,
  SdThemeComponent,
  SizesEnum,
  ThemesCssVariablesMapper,
  ThemesEnum
} from '@seb/web-design-system/core';

@Component({
  selector: 'sd-arguments',
  templateUrl: './arguments.component.html',
  styleUrls: ['./arguments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdBannerComponent,
    NgStyle,
    SdRichParagraphComponent
  ]
})
export class SdArgumentsComponent extends SdThemeComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  @Input()
  isLoading = false;

  @Input()
  data: IArguments;

  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevel = 3;

  /**
   * Determines whether the title is inside a hx tag or in a p tag for the title
   */
  @Input()
  isDescription = false;

  /**
   * Allow to define if the text is aligned to the left or the right, or even centered
   * * @param {string} left | right | center
   */
  @Input()
  textConfig: PositionEnum.left | PositionEnum.right | PositionEnum.center =
    PositionEnum.left;

  /**
   * Allow to define if the text is aligned on the left or on right, or even centered (only if there is no secondary media)
   * * @param {string} left | right | center
   */
  @Input()
  layoutTextConfig:
    | PositionEnum.left
    | PositionEnum.right
    | PositionEnum.center = PositionEnum.left;

  /**
   * Allow to define either the theme is dark or light (default)
   * * @param {string} dark | light | brand | & marketing
   */
  @Input()
  themesConfig: ThemesEnum | BrandThemesEnum = ThemesEnum.light;

  @Input()
  imageRatio: ImageRatioEnum;

  @Input()
  hasGradient = false;

  @Input()
  embeddedOpacity: OpacityEnum = OpacityEnum.v_100;

  @Input()
  isSmall = false;

  @Input()
  layout: ArgumentsLayoutEnum = ArgumentsLayoutEnum.default;

  @Input()
  twicPicsAutoSize = true;

  @ViewChild('mediaBackground')
  mediaBackground: SdBannerComponent;

  @ViewChild('richParagraph')
  richParagraph: SdRichParagraphComponent;

  @ViewChild('contentWrapper')
  contentWrapper: ElementRef;

  @ViewChildren('mediasInnerSlider', { read: ElementRef })
  mediasInnerSlider: QueryList<ElementRef>;

  @Output()
  videoPlayed = new EventEmitter<{ status: string; title: string }>();

  @Output()
  videoEnded = new EventEmitter<{ status: string; title: string }>();

  @Output()
  clicked = new EventEmitter<IExtendedLink | ILinkMedia>();

  @Input()
  class = ''; // override the standard class attr with a new one.
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.isMobile$ ? 'is-center-align' : 'is-' + this.textConfig + '-align',
      this.isSmall ? 'is-small' : '',
      this.themesConfig
        ? 'is-theme-' + this.themesConfig
        : 'is-theme-' + ThemesEnum.light,
      this.realLayoutTextConfig
        ? 'is-layout-' + this.realLayoutTextConfig
        : 'is-layout-' + PositionEnum.left,
      this.layout ? 'is-' + this.layout : 'is-layout-default',
      this.isSpaced ? 'is-spaced' : '',
      this.layout === ArgumentsLayoutEnum.divided && this.hasGradient
        ? 'has-gradient'
        : '',
      this.data?.media || this.data?.mediasSlider ? 'has-background' : '',
      this.data?.hasAlphaHeader ? 'has-alpha-header' : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  positionEnum = PositionEnum;
  sizesEnum = SizesEnum;
  themesEnum = ThemesEnum;
  ratioEnum = ImageRatioEnum;
  argumentsLayoutEnum = ArgumentsLayoutEnum;
  isMobile$: boolean;
  isDesktopLarge$: boolean;
  isDevice$: boolean;
  subscriptions = new Subscription();
  isSpaced = false;
  realLayoutTextConfig: PositionEnum.left | PositionEnum.right | PositionEnum.center = PositionEnum.left;
  imageAbsolute = 'auto';
  activeIndex = 0;

  constructor(
    private renderer: Renderer2,
    private deviceService: DeviceService,
    private cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x) => {
        this.isMobile$ = x;
        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.deviceService.isDevice.subscribe((x) => {
        this.isDevice$ = x;
        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.deviceService.isDesktopLarge.subscribe((x) => {
        this.isDesktopLarge$ = x;
        this.cdRef.markForCheck();
      })
    );

    if (isPlatformBrowser(this.platformId)) {
      this.manageHeight();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    this.isSpaced =
      !!this.data?.secondaryMedia &&
      this.layout === ArgumentsLayoutEnum.default;

    if (this.layoutTextConfig === PositionEnum.center) {
      if (this.layout === ArgumentsLayoutEnum.divided || this.isSpaced) {
        this.realLayoutTextConfig = PositionEnum.left;
      } else {
        this.realLayoutTextConfig = this.layoutTextConfig;
      }
    }

    if (this.isSpaced && this.layoutTextConfig === PositionEnum.center) {
      this.realLayoutTextConfig = PositionEnum.left;
    } else {
      this.realLayoutTextConfig = this.layoutTextConfig;
    }

    if (this.data?.mediasSlider && !this.data?.content?.linksImages) {
      this.data.content.linksImages =
        this.mediasToILinkMedias(this.data?.mediasThumbnailsSlider) ||
        this.mediasToILinkMedias(this.data?.mediasSlider);
    }
  }

  ngAfterViewChecked(): void {
    this.isSpaced =
      !!this.data?.secondaryMedia &&
      this.layout === ArgumentsLayoutEnum.default;

    if (this.layoutTextConfig === PositionEnum.center) {
      if (this.layout === ArgumentsLayoutEnum.divided || this.isSpaced) {
        this.realLayoutTextConfig = PositionEnum.left;
      } else {
        this.realLayoutTextConfig = this.layoutTextConfig;
      }
    }

    if (this.isSpaced && this.layoutTextConfig === PositionEnum.center) {
      this.realLayoutTextConfig = PositionEnum.left;
    } else {
      this.realLayoutTextConfig = this.layoutTextConfig;
    }

    if (isPlatformBrowser(this.platformId)) {
      this.manageHeight();
      if (this.activeBrand) {
        this.updateMainBackground(ThemesCssVariablesMapper.mapBrandTheme(this.themesConfig));
        if (this.hasGradient) {
          this.updateMainBackground(
            ThemesCssVariablesMapper.mapBrandTheme(this.themesConfig),
            '.has-gradient > .c__content > sd-banner',
            true
          );
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onClick(event: { index: number; link: IExtendedLink | ILinkMedia }): void {
    this.activeIndex = event.index;
    this.clicked.emit(event.link);
  }

  onVideoPlayed(): void {
    this.videoPlayed.emit({
      status: 'play',
      title: this.data?.content?.heading?.data?.title
    });
  }

  onVideoEnded(): void {
    this.videoEnded.emit({
      status: 'ended',
      title: this.data?.content?.heading?.data?.title
    });
  }

  private mediasToILinkMedias(medias: IMedia[]): ILinkMedia[] {
    const newMediasLinks: ILinkMedia[] = [];
    medias.forEach((media: IMedia, index: number) => {
      if (!media && (!media?.url || !media?.urlTwicPic)) {
        newMediasLinks.push({
          url: undefined,
          linkName: null,
          media: {
            url: null
          }
        });
      } else {
        newMediasLinks.push({
          url: undefined,
          linkName: media.altText,
          disable: index !== 0,
          media
        });
      }
    });
    return newMediasLinks;
  }

  private manageHeight() {
    if (this.data?.content?.linksImages || this.data?.mediasSlider) {
      if (this.mediasInnerSlider) {
        const bodySelector = this.mediasInnerSlider.toArray();
        if (bodySelector) {
          this.setHeight(bodySelector);
        }
      }
    }
  }

  private setHeight(bodySelector: ElementRef[]): void {
    const maxHeight = this.getMaxHeight(bodySelector);
    if (maxHeight !== 0) {
      bodySelector.forEach((el) => {
        this.renderer.setStyle(
          el.nativeElement,
          'height',
          maxHeight + 'px'
        );
        this.renderer.setStyle(
          this.contentWrapper.nativeElement,
          'height',
          maxHeight + 'px',
        );
      });
    }
  }

  private getMaxHeight(bodySelector: ElementRef[]): number {
    let maxHeight = 0;
    bodySelector.forEach((el) => {
      const thisH = el.nativeElement.clientHeight;
      if (thisH > maxHeight) {
        maxHeight = thisH;
      }
    });
    return maxHeight;
  }
}
